import { FC, ReactNode, isValidElement, useMemo } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import { AuditTrialType } from '@dynamika-shared/types/auditTrial';

import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import {
  detailsPreparation,
  hasNestedPayload,
  jsonChildren,
  payloadsPreparation,
  removeSkippedFields,
} from '../utils/auditInfoHelpers';
import { Detail } from './types';

export type AuditInfoProps = {
  open: boolean;
  info: Partial<AuditTrialType>;
  close?: () => void;
};

const DetailValueFromArray = ({
  element,
  index,
  detail,
}: {
  element: unknown
  index: number;
  detail: Detail;
}) => {
  // NOTE: upstream paths (v3 details, nested payloads) sometimes hand us
  // already-rendered React elements; cleaning them would walk React internals
  // (Fiber `_owner` / `alternate` / `return`) and blow the call stack.
  if (isValidElement(element)) {
    return <>{element}</>;
  }

  const cleaned = removeSkippedFields(element);
  if (
    typeof cleaned !== 'object' ||
    cleaned === null ||
    Object.values(cleaned).filter(Boolean).length === 0
  ) {
    return null;
  }

  return (
    <div className='ml-[4rem]' key={index + detail.label}>
      {'{'}&nbsp;{'\n'}
      {jsonChildren(cleaned)}
      {'\n'}
      {'}'} ,
    </div>
  );
};

const DetailEl = ({ detail, index }: { detail: Detail; index: number }) => {
  return (
    <div key={detail.key + index} className='mt-[2.2rem] pl-[1.2rem]'>
      <span className='mr-[0.8rem] text-pastelBlue'>{index + 1}</span>
      <span className='text-[#1C1C1C] text-1624 font-medium'>
        "{detail.key}":
      </span>{' '}
      <span className='text-1624 font-medium text-vividTangelo'>
        {Array.isArray(detail.value) &&
          detail?.value?.map((element, index) => {
            return (
              <DetailValueFromArray
                key={index}
                element={element}
                index={index}
                detail={detail}
              />
            );
          })}
        {!Array.isArray(detail.value) && (detail.value as ReactNode)}
      </span>{' '}
    </div>
  );
};

export const AuditInfo: FC<AuditInfoProps> = ({ open, info, close }) => {
  const rawDetails = useMemo(() => {
    if (hasNestedPayload(info)) {
      const { payloads, ...otherInfo } = info;
      return [
        ...detailsPreparation(otherInfo),
        ...payloadsPreparation(payloads),
      ];
    }
    return detailsPreparation(info);
  }, [info]);

  const details = useMemo(() => {
    const map = new Map<string, Detail>();
    rawDetails.forEach((detail) => {
      map.set(detail.key, detail);
    });
    return Array.from(map.values());
  }, [rawDetails]);

  const visibleDetails = useMemo(
    () => details.filter(detail => detail.key !== 'signature'),
    [details]
  );

  /**
   * Placeholder for records without renderable details
   *
   * Plain terms
   * -----------
   * Some newer (v3) audit records arrive with an empty "details" section
   * (e.g. "QC task status updated", "Subject Modified"), so the drawer has
   * nothing to list. Instead of a blank panel, we tell the reviewer
   * explicitly that there is nothing more to show (D7-7453).
   *
   * Technical
   * ---------
   * `detailsPreparation` returns [] for v3 records whose `details` object
   * is empty or missing — the v3 path hides the envelope keys and surfaces
   * only `details`. `signature` is filtered out before the emptiness check
   * so a signature-only record also shows the placeholder.
   */
  const showNoDetailsPlaceholder = visibleDetails.length === 0;

  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-0 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem] !border-transparent',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={'Audit Trail'}
      open={open}
      width={464}
      placement='right'
      onClose={close}
      closeIcon={<CloseOutlined />}
    >
      {!showNoDetailsPlaceholder && (
        <div className='p-[12px] bg-aliceBlue border rounded-[8px] border-transparent'>
          <h3 className='text-[18px] font-medium leading-6'>Information</h3>
        </div>
      )}
      {showNoDetailsPlaceholder ? (
        <div
          className='mt-[2.2rem] pl-[1.2rem] text-1624 font-medium text-[#1C1C1C]'
          data-testid='audit-info-no-details'
        >
          No additional details
        </div>
      ) : (
        visibleDetails.map((detail, ind) => (
          <DetailEl key={detail.key} detail={detail} index={ind} />
        ))
      )}
    </Drawer>
  );
};
